import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, unref as _unref, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, renderSlot as _renderSlot, normalizeStyle as _normalizeStyle, createVNode as _createVNode } from "vue"

const _hoisted_1 = ["src"]
const _hoisted_2 = ["href", "aria-label"]
const _hoisted_3 = { class: "px-6 lg:hidden" }
const _hoisted_4 = {
  key: 0,
  class: "flex w-full gap-2"
}
const _hoisted_5 = {
  key: 1,
  class: "mt-6 flex justify-between gap-6"
}
const _hoisted_6 = ["disabled"]

import { computed, nextTick, onMounted, provide, ref } from 'vue';
import ScrollTrigger from 'gsap/ScrollTrigger';
import gsap from 'gsap';
import { ScrollItem } from '@/types';
import BizIcon from '@/components/base/BizIcon.vue';
import BizAi from '@/components/BizAiElement.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'VideoScrolling',
  props: {
    video: null,
    videoAiGenerated: { type: Boolean, default: false },
    aiTooltipText: { default: 'This video was generated by AI' },
    editMode: { type: Boolean, default: false },
    contrast: { type: Boolean, default: false }
  },
  setup(__props: any) {

const props = __props as { video: string, videoAiGenerated: boolean, aiTooltipText: string, editMode: boolean, contrast: boolean }



const scrollGsap = ref<ScrollTrigger | null>(null);

const el = ref<HTMLElement | null>(null);
const itemEl = ref<HTMLDivElement[] | null>(null);

const videoEl = ref<HTMLVideoElement | null>(null);
const videoLoaded = ref(false);
const duration = ref(0);
const seek = ref(0);

const items = ref<ScrollItem[]>([]);
const paddingHeight = ref(298);

let mediaQueryBreakpoint: MediaQueryList | null = null;
let observer: IntersectionObserver | null = null;

gsap.registerPlugin(ScrollTrigger);

provide('registerItem', registerItem);

onMounted(() => {
    mediaQueryBreakpoint = window.matchMedia('(min-width: 1024px');
    if (!props.editMode) {
        onResize();
        mediaQueryBreakpoint.addEventListener('change', onResize);
        window.addEventListener('resize', calcHeight);
    }
});

const currentIndex = computed(() => {
    let index = -1;
    items.value.forEach((item, itemIndex) => {
        if (isApplicable(item, false)) {
            index = itemIndex;
        }
    });
    return index;
});

const lastIndex = computed(() => {
    let index = -1;
    items.value.forEach((item, itemIndex) => {
        if (isApplicable(item, true)) {
            index = itemIndex;
        }
    });
    return index;
});

function getIndicatorWidth(index: number): number {
    if (!videoEl.value || lastIndex.value < index) return 0;
    if (lastIndex.value > index) return 100;
    const currentTime = videoEl.value.currentTime;
    const startTime = items.value[lastIndex.value].start;
    // case 1: end time is set -> take end time
    // case 2: item is last item -> take end of video
    // default case: take start of next item
    const endTime =
        items.value[lastIndex.value].end > -1
            ? items.value[lastIndex.value].end
            : items.value.length === lastIndex.value + 1
            ? videoEl.value.duration
            : items.value[lastIndex.value + 1].start;
    return Math.floor(((currentTime - startTime) / (endTime - startTime)) * 100);
}

function registerItem(item: ScrollItem) {
    items.value.push(item);
}

function initScroll() {
    killScroll();
    requestAnimationFrame(() => {
        scrollGsap.value = ScrollTrigger.create({
            trigger: el.value,
            start: 'top top',
            end: '+=5000',
            scrub: true,
            pin: true,
            refreshPriority: 1,
            invalidateOnRefresh: true,
            onUpdate: (self: { progress: number }) => {
                if (videoLoaded.value) {
                    seek.value = self.progress * duration.value;
                    videoEl.value.currentTime = seek.value;
                }
            }
        });
        ScrollTrigger.refresh();
    });
}

function isApplicable(item: ScrollItem, ignoreEnd: boolean): boolean {
    if (item.end > 0 && !ignoreEnd) {
        return item.start <= seek.value && item.end > seek.value;
    }
    return item.start <= seek.value;
}

function onVideoLoad(): void {
    duration.value = videoEl.value.duration;
    videoLoaded.value = true;
    videoEl.value.addEventListener('timeupdate', () => {
        seek.value = videoEl.value.currentTime;
    });
}

function prev(): void {
    if (lastIndex.value === 0) {
        videoEl.value.currentTime = 0;
    } else if (lastIndex.value + 1 === items.value.length && seek.value > duration.value - 0.5) {
        videoEl.value.currentTime = items.value[lastIndex.value].start;
    } else {
        videoEl.value.currentTime = items.value[lastIndex.value - 1].start;
    }
    videoEl.value.play();
}

function next(): void {
    videoEl.value.currentTime = items.value[lastIndex.value + 1].start;
    videoEl.value.play();
}

function observeVideo(): void {
    const options = {
        rootMargin: '0px',
        scrollMargin: '0px',
        threshold: 1.0
    };

    observer = new IntersectionObserver(function (entries) {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                videoEl.value.play();
            } else {
                videoEl.value.pause();
            }
        });
    }, options);

    observer.observe(videoEl.value);
}

function killScroll(): void {
    if (scrollGsap.value) {
        scrollGsap.value.kill();
        scrollGsap.value = null;
        ScrollTrigger.refresh();
    }
}

function calcHeight(): void {
    let height = 0;
    if (itemEl.value) {
        itemEl.value.forEach(item => {
            const elHeight = item.offsetHeight;
            if (elHeight > height) height = elHeight;
        });
    }
    paddingHeight.value = height;
}

function onResize(): void {
    nextTick(() => {
        if (!el.value) return;
        if (mediaQueryBreakpoint?.matches) {
            if (!scrollGsap.value) {
                initScroll();
            }
            if (observer) {
                observer.disconnect();
                observer = null;
            }
            if (videoLoaded.value) {
                videoEl.value.pause();
            }
        } else {
            if (scrollGsap.value) {
                killScroll();
            }
            if (!observer) {
                observeVideo();
            }
            calcHeight();
        }
    });
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", {
      class: "relative",
      ref_key: "el",
      ref: el
    }, [
      (__props.videoAiGenerated)
        ? (_openBlock(), _createBlock(BizAi, {
            key: 0,
            type: "icon",
            show: __props.videoAiGenerated,
            position: "top-right",
            "position-mobile": "top-right",
            "z-index": "z-20",
            "tooltip-position": "bottom",
            "tooltip-text": __props.aiTooltipText,
            "icon-color": "text-white"
          }, null, 8, ["show", "tooltip-text"]))
        : _createCommentVNode("", true),
      _createElementVNode("video", {
        class: "pointer-events-none w-full lg:h-screen lg:object-cover lg:object-center",
        ref_key: "videoEl",
        ref: videoEl,
        onLoadeddata: onVideoLoad,
        muted: ""
      }, [
        _createElementVNode("source", {
          src: __props.video,
          type: "video/mp4"
        }, null, 8, _hoisted_1)
      ], 544),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(items.value, (item, index) => {
        return (_openBlock(), _createElementBlock("div", {
          key: index,
          class: _normalizeClass([{
                        'pointer-events-none opacity-0': index !== _unref(currentIndex),
                        'bg-white/50': !__props.contrast,
                        'bg-white/20 text-white': __props.contrast
                    }, "absolute left-3 right-3 top-full max-h-[298px] -translate-y-8 transform px-3 pt-6 pb-10 backdrop-blur-md transition-opacity duration-1000 lg:left-[240px] lg:top-[125px] lg:block lg:max-h-[429px] lg:max-w-[564px] lg:p-10"]),
          ref_for: true,
          ref_key: "itemEl",
          ref: itemEl
        }, [
          _createElementVNode("h3", {
            class: _normalizeClass(["text-lg lg:text-6xl-quote-long", { 'text-gray-500': !__props.contrast }])
          }, _toDisplayString(item.title), 3),
          _createElementVNode("p", {
            class: _normalizeClass(["mt-3 text-sm line-clamp-[8] lg:mt-6 lg:text-md", { 'text-gray-400': !__props.contrast }])
          }, _toDisplayString(item.text), 3),
          (item.link && item.linkLabel)
            ? (_openBlock(), _createElementBlock("a", {
                key: 0,
                class: _normalizeClass(["relative mt-3 inline-block border-b px-1.5 text-sm transition-colors lg:mt-6 lg:text-md", {
                            'border-black text-gray-500 hover:text-gray-400': !__props.contrast,
                            'hover:text-gray-100': __props.contrast
                        }]),
                href: item.link,
                target: "_self",
                "aria-label": item.linkLabel
              }, [
                _createElementVNode("span", null, _toDisplayString(item.linkLabel), 1),
                _createElementVNode("div", {
                  class: _normalizeClass(["absolute bottom-0 left-0 h-1/3 w-px", { 'bg-black': !__props.contrast, 'bg-white': __props.contrast }])
                }, null, 2)
              ], 10, _hoisted_2))
            : _createCommentVNode("", true)
        ], 2))
      }), 128)),
      _renderSlot(_ctx.$slots, "default")
    ], 512),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", {
        style: _normalizeStyle({ height: `${paddingHeight.value}px` })
      }, null, 4),
      (items.value.length > 1)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(items.value.length, (n, i) => {
              return (_openBlock(), _createElementBlock("div", {
                key: i,
                class: _normalizeClass(["relative h-1 flex-grow overflow-hidden rounded-full", { 'bg-black/50': !__props.contrast, 'bg-white/50': __props.contrast }])
              }, [
                _createElementVNode("div", {
                  class: _normalizeClass(["h-full w-full transition-[max-width] duration-[250ms] ease-linear", {
                            'bg-black': !__props.contrast,
                            'bg-white': __props.contrast
                        }]),
                  style: _normalizeStyle({
                            maxWidth: `${getIndicatorWidth(i)}%`
                        })
                }, null, 6)
              ], 2))
            }), 128))
          ]))
        : _createCommentVNode("", true),
      (items.value.length > 1)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            _createElementVNode("button", {
              class: _normalizeClass(["flex h-9 w-9 items-center justify-center border", {
                        'border-gray-400 text-gray-400 disabled:border-gray-400/50 disabled:text-gray-400/50':
                            !__props.contrast,
                        'border-white text-white disabled:border-white/50 disabled:text-white/50':
                            __props.contrast
                    }]),
              onClick: prev
            }, [
              _createVNode(BizIcon, {
                name: "chevron-right",
                class: "h-3 w-3 rotate-180 transform fill-current"
              })
            ], 2),
            _createElementVNode("button", {
              class: _normalizeClass(["flex h-9 w-9 items-center justify-center border", {
                        'border-gray-400 text-gray-400 disabled:border-gray-400/50 disabled:text-gray-400/50':
                            !__props.contrast,
                        'border-white text-white disabled:border-white/50 disabled:text-white/50':
                            __props.contrast
                    }]),
              disabled: items.value.length === _unref(lastIndex) + 1,
              onClick: next
            }, [
              _createVNode(BizIcon, {
                name: "chevron-right",
                class: "h-3 w-3 fill-current"
              })
            ], 10, _hoisted_6)
          ]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})